if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.initNavButtons();
        this.initThumbnailClicks();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      initThumbnailClicks() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const targetId = mediaToSwitch.getAttribute('data-target');
          if (!targetId) return;

          const clickable = mediaToSwitch.querySelector('button') || mediaToSwitch;
          clickable.addEventListener('click', (event) => {
            event.preventDefault();
            this.setActiveMedia(targetId, false);
          });
        });
      }

      initNavButtons() {
        // ── Main Image Viewer Overlay Arrows (Left < and Right >) ──
        if (this.elements.viewer) {
          const viewerPrev = this.elements.viewer.querySelector('.slider-button--prev[name="previous"]');
          const viewerNext = this.elements.viewer.querySelector('.slider-button--next[name="next"]');
          if (viewerPrev) this.enableButton(viewerPrev, () => this.navigateMedia(-1));
          if (viewerNext) this.enableButton(viewerNext, () => this.navigateMedia(1));
        }

        // ── Thumbnail Strip Arrows (Prev < and Next >) ──
        if (this.elements.thumbnails) {
          const thumbPrev = this.elements.thumbnails.querySelector('.slider-button--prev[name="previous"]');
          const thumbNext = this.elements.thumbnails.querySelector('.slider-button--next[name="next"]');
          if (thumbPrev) this.enableButton(thumbPrev, () => this.scrollThumbnails(-1));
          if (thumbNext) this.enableButton(thumbNext, () => this.scrollThumbnails(1));
        }
      }

      enableButton(button, callback) {
        if (!button) return;
        button.removeAttribute('disabled');
        
        button.onclick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          callback();
        };

        const observer = new MutationObserver(() => {
          if (button.hasAttribute('disabled')) {
            button.removeAttribute('disabled');
          }
        });
        observer.observe(button, { attributes: true, attributeFilter: ['disabled'] });
      }

      navigateMedia(direction) {
        if (!this.elements.viewer) return;
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        if (mediaItems.length <= 1) return;

        let activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        if (activeIndex === -1) activeIndex = 0;

        let newIndex = activeIndex + direction;
        if (newIndex < 0) {
          newIndex = mediaItems.length - 1;
        } else if (newIndex >= mediaItems.length) {
          newIndex = 0;
        }

        const targetMediaId = mediaItems[newIndex].getAttribute('data-media-id');
        if (targetMediaId) {
          this.setActiveMedia(targetMediaId, false);
        }
      }

      scrollThumbnails(direction) {
        if (!this.elements.thumbnails) return;
        const list = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (!list) return;

        list.scrollBy({ left: direction * 180, behavior: 'smooth' });
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails || !event.detail || !event.detail.currentElement) return;
        const mediaId = event.detail.currentElement.getAttribute('data-media-id');
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer) return;

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          const position = activeThumbnail ? activeThumbnail.getAttribute('data-media-position') : 1;
          this.announceLiveRegion(activeMedia, position);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        const btn = thumbnail.querySelector('button') || thumbnail;
        btn.setAttribute('aria-current', 'true');

        const list = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (list) {
          const thumbLeft = thumbnail.offsetLeft - list.offsetLeft;
          const thumbRight = thumbLeft + thumbnail.offsetWidth;
          if (thumbLeft < list.scrollLeft) {
            list.scrollTo({ left: thumbLeft, behavior: 'smooth' });
          } else if (thumbRight > list.scrollLeft + list.clientWidth) {
            list.scrollTo({ left: thumbRight - list.clientWidth, behavior: 'smooth' });
          }
        }
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        if (this.elements.viewer.sliderItems) {
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
      }
    }
  );
}

